import React, { useState } from 'react';
import {
  useGetItemsIssuedQuery,

} from '../../../redux/vmsApi';
import type { GetItemsIssuedApiArg } from '../../../redux/vmsApi';
import {
  Box, Button, FormControl, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { formatCurrency } from '../../../utils/currencyUtil';
import {camelCaseToSpaced} from "../../../utils/formattingUtil";

const ItemsIssuedList: React.FC = () => {
  const [filters, setFilters] = useState({
     itemCode: '',
     itemDescription: '',
     unitName: '',
     locationDescription: '',
     developmentAllocation: '',
     projectAllocation: '',
     correctProjectAllocation: '',
     borrowedIssue: '',
     supplier: '',
     purchaseOrderNumber: '',
     currency: ''
   });
 
   const [page, setPage] = useState(0);
   const [pageSize, setPageSize] = useState(10);
   const [apiParams, setApiParams] = useState<GetItemsIssuedApiArg>({
     skip: 0,
     limit: 10,
   });
 
   const { data: itemIssues, isLoading, isError } = useGetItemsIssuedQuery(apiParams);
 
   const handleFilterChange = (field: string, value: string) => {
     setFilters(prev => ({ ...prev, [field]: value }));
   };
 
   const handleApplyFilters = () => {
     const { borrowedIssue, ...apiFilterValues } = filters;

     const cleanedFilterValues = Object.fromEntries(
       Object.entries(apiFilterValues).filter(([, value]) => value.trim() !== '')
     ) as Omit<GetItemsIssuedApiArg, 'isIncorrectIssue' | 'limit' | 'skip'>;

     setApiParams({
       ...cleanedFilterValues,
       isIncorrectIssue: filters.borrowedIssue === ''
         ? undefined
         : filters.borrowedIssue === 'true',
       skip: 0,
       limit: pageSize,
     });
     setPage(0);
   };
 
   const handlePageChange = (newPage: number) => {
     setPage(newPage);
     setApiParams(prev => ({ ...prev, skip: newPage * pageSize }));
   };
 
   const handlePageSizeChange = (event: any) => {
     const newSize = Number(event.target.value);
     setPageSize(newSize);
     setPage(0);
     setApiParams(prev => ({ ...prev, limit: newSize, skip: 0 }));
   };
 
   const filterFields = [
     'itemCode', 'itemDescription', 'unitName', 'locationDescription',
     'developmentAllocation', 'projectAllocation', 'correctProjectAllocation', 'supplier', 'purchaseOrderNumber', 'currency'
   ];
 
   if (isLoading) return <div>Loading...</div>;
   if (isError) return <div>Error loading item locations.</div>;
 
   return (
     <React.Fragment>
       <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: '#f9f9f9', boxShadow: 3 }}>
         <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Filter
         </Typography>
         <Box display="flex" flexWrap="wrap" gap={2}>
           {filterFields.map(field => (
             <TextField
               key={field}
               label={field === 'correctProjectAllocation' ? 'Intended Project' : camelCaseToSpaced(field)}
               variant="outlined"
               size="small"
               value={(filters as any)[field]}
               onChange={(e) => handleFilterChange(field, e.target.value)}
               sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
               fullWidth
             />
           ))}
           <TextField
             select
             label="Borrowed Allocation"
             variant="outlined"
             size="small"
             value={filters.borrowedIssue}
             onChange={(e) => handleFilterChange('borrowedIssue', e.target.value)}
             sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
             fullWidth
           >
             <MenuItem value="">Any</MenuItem>
             <MenuItem value="true">Yes</MenuItem>
             <MenuItem value="false">No</MenuItem>
           </TextField>
         </Box>
         <Stack direction="row" spacing={2} mt={2}>
           <Button
             variant="outlined"
             size="large"
             onClick={handleApplyFilters}
             sx={{
               textTransform: 'none',
               fontWeight: 'bold',
               color: '#1976d2',
               borderColor: '#1976d2',
               '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#1565c0' },
             }}
           >
             Apply Filters
           </Button>
         </Stack>
       </Box>
 
       <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
         <Table>
           <TableHead sx={{ backgroundColor: '#1976d2' }}>
             <TableRow>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Item Code</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Item Description</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Housing Unit</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Allocations</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Purchase Order</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Item Quantity</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rate </TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Discount </TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>VAT</TableCell>
               <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Value</TableCell>
             </TableRow>
           </TableHead>
           <TableBody>
             {itemIssues && itemIssues.length > 0 ? (
               itemIssues.map((loc: any, index: number) => (
                 <TableRow key={loc.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                   <TableCell>{loc.itemCode}</TableCell>
                   <TableCell>{loc.itemDescription}</TableCell>
                   <TableCell>{loc.unitName} </TableCell>
                   <TableCell>
                     Dev: {loc.developmentAllocation?.name || '-'}<br />
                     Proj: {loc.projectAllocation?.name || '-'}
                   </TableCell>
                   <TableCell>{loc.supplier}</TableCell>
                   <TableCell>{loc.purchaseOrderNumber}</TableCell>
                   <TableCell>{loc.quantity} </TableCell>
                   <TableCell>{formatCurrency(loc.rate, loc.currency || 'usd')}</TableCell>
                   <TableCell>{formatCurrency(loc.itemDiscount, loc.currency || 'usd')}</TableCell>
                   <TableCell>
                     {formatCurrency((loc.VAT || 0) * (loc.quantity || 0) , loc.currency || 'usd')}
                   </TableCell>
                   
                    <TableCell>{formatCurrency(loc.totalValue, loc.currency || 'usd')}</TableCell>
                 </TableRow>
               ))
             ) : (
               <TableRow>
                 <TableCell colSpan={8} align="center">No matching item issued found.</TableCell>
               </TableRow>
             )}
           </TableBody>
         </Table>
       </TableContainer>
 
       <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
         <Button onClick={() => handlePageChange(Math.max(page - 1, 0))} disabled={page === 0} variant="contained" sx={{ marginRight: 2 }}>
           Previous
         </Button>
         <Typography variant="body1">Page {page + 1}</Typography>
         <Button onClick={() => handlePageChange(page + 1)} disabled={itemIssues?.length === undefined || itemIssues.length < pageSize} variant="contained" sx={{ marginLeft: 2 }}>
           Next
         </Button>
       </Box>
 
       <Box mt={2} display="flex" justifyContent="center" alignItems="center">
         <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
         <FormControl variant="outlined" size="small">
           <Select
             value={pageSize}
             onChange={handlePageSizeChange}
             sx={{ backgroundColor: '#f0f4f7' }}
           >
             <MenuItem value={5}>5</MenuItem>
             <MenuItem value={10}>10</MenuItem>
             <MenuItem value={20}>20</MenuItem>
           </Select>
         </FormControl>
       </Box>
     </React.Fragment>
   );
 };

export default ItemsIssuedList;
